import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  Button, TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@material-ui/core';

import { VIEW_MODE, EDIT_MODE } from '../../../utils/const/taskModes';
import { NONE, PRICE, isShareKpiType } from '../../../utils/const/teleaudit';

import useStyles from '../styles';
import ImageSettingsModal from '../ImageSettingsModal';
import QuestionShell from '../QuestionShell';
import useQuestionForm from '../QuestionShell/useQuestionForm';

function ImageAnswer({
  identifier, pages, info, handleDeleteQuestion, handleDuplicateQuestion,
  mode, questionOptions = [], surveyAiAnalysisRequired = false,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [openSettings, setOpenSettings] = useState(false);
  const [openSettingSo, setOpenSettingSo] = useState(false);
  const [priceKpiEnabled, setPriceKpiEnabled] = useState(false);
  const [kpiDetails, setKpiDetails] = useState([]);
  const [requiresTeleaudit, setRequiresTeleaudit] = useState(false);
  const [shareOfShelfType, setShareOfShelfType] = useState('none');
  const [target, setTarget] = useState(0);
  const [selectedQuestionOption, setSelectedQuestionOption] = useState('');
  const [aiAnalysisRequired, setAiAnalysisRequired] = useState(
    info ? info.aiAnalysisRequired || false : pages[identifier - 1]?.aiAnalysisRequired || false,
  );
  const [guideline, setGuideline] = useState(
    info ? info.guideline || '' : pages[identifier - 1]?.guideline || '',
  );
  const [imageQuality, setImageQuality] = useState(
    info ? info.imageQuality : pages[identifier - 1]?.imageQuality || '',
  );

  const question = useQuestionForm({
    identifier, pages, info, autoLoadMedia: false,
  });
  const {
    image, setImage, setEmailsArray, setSendEmail, setDescription,
    scheduleWrite, questionLogic, syncLocalField,
  } = question;

  useEffect(() => {
    if (info !== undefined) {
      setImage({
        ...image,
        url: info.imageUrl,
        name: info.imageKey,
      });
      setRequiresTeleaudit(info.requiresTeleaudit);
      setSelectedQuestionOption(info.surveyTeleauditId || '');
      if (info.TeleauditKpis && info.requiresTeleaudit) {
        const kpiDetailsArray = [];
        let count = 0;
        info.TeleauditKpis.forEach((teleauditKpi) => {
          if (isShareKpiType(teleauditKpi.KpiDetail.teleauditType)) {
            setShareOfShelfType(teleauditKpi.KpiDetail.teleauditType);
            setTarget(teleauditKpi.KpiDetail.target);
            return;
          }
          if (teleauditKpi.KpiDetail.teleauditType === PRICE) {
            setPriceKpiEnabled(true);
            return;
          }
          count += 1;
          kpiDetailsArray.push({
            title: teleauditKpi.KpiDetail.name,
            description: teleauditKpi.KpiDetail.description,
            teleauditType: teleauditKpi.KpiDetail.teleauditType,
            image: teleauditKpi.KpiDetail.imageUrl || '',
            // Id local, sólo para la lista del modal.
            id: count,
            // Id del KPI en la base: se devuelve al API para actualizarlo en
            // vez de crear uno nuevo.
            teleauditKpiId: teleauditKpi.id,
          });
        });
        setKpiDetails(kpiDetailsArray);
      }
      if (
        info.QuestionEmails !== undefined
        && info.QuestionEmails.length !== 0) {
        setEmailsArray(info.QuestionEmails[0].emails.split(','));
      }
      setSendEmail(info.sendEmail);
    } else if (pages) {
      setRequiresTeleaudit(pages[identifier - 1].requiresTeleaudit);
      setSelectedQuestionOption(pages[identifier - 1].surveyTeleauditId || '');
      if (pages[identifier - 1].binaryKpis && pages[identifier - 1].requiresTeleaudit) {
        const kpiDetailsArray = [];
        let count = 0;
        pages[identifier - 1].binaryKpis.forEach((binaryKpi) => {
          if (isShareKpiType(binaryKpi.teleauditType) || binaryKpi.teleauditType === PRICE) return;
          count += 1;
          kpiDetailsArray.push({
            title: binaryKpi.title,
            description: binaryKpi.description,
            teleauditType: binaryKpi.teleauditType,
            image: binaryKpi.image || '',
            id: count,
            teleauditKpiId: binaryKpi.id || null,
          });
        });
        setKpiDetails(kpiDetailsArray);
        const pageShareKpi = pages[identifier - 1].shareKpi;
        if (pageShareKpi?.teleauditType && pageShareKpi.teleauditType !== NONE) {
          setShareOfShelfType(pageShareKpi.teleauditType);
          setTarget(pageShareKpi.target);
        }
      }
      setPriceKpiEnabled(pages[identifier - 1].priceKpi?.teleauditType === PRICE);
    } else {
      if (
        pages[identifier - 1].questionEmails !== undefined
        && pages[identifier - 1].questionEmails.length !== 0) {
        setEmailsArray(pages[identifier - 1].questionEmails[0].emails.split(','));
      }
      setSendEmail(pages[identifier - 1].sendEmail);
    }

    if (pages && pages[identifier - 1].imageUrl) {
      setImage({
        ...image,
        url: pages[identifier - 1].imageUrl,
        name: pages[identifier - 1].imageKey,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sólo escribe las claves que realmente cambiaron: el modal de KPIs y los
  // selects disparan cambios con los mismos valores y no hace falta reescribir.
  const applyChange = useCallback((changes) => {
    const current = pages[identifier - 1];
    const filtered = {};
    Object.keys(changes).forEach((k) => {
      if (current[k] !== changes[k]) {
        filtered[k] = changes[k];
      }
    });
    if (Object.keys(filtered).length === 0) return;

    // eslint-disable-next-line no-param-reassign
    pages[identifier - 1] = {
      ...current,
      ...filtered,
      questionChoices: null,
      questionLogic,
    };
  }, [identifier, pages, questionLogic]);

  const handleOnChange = useCallback((event) => {
    const {
      id, name, value, imageUrl,
    } = event.target;

    const key = name === 'imageQuality' ? name : id;

    if (key === 'title' || key === 'shortQuestion' || key === 'description') {
      syncLocalField(key, value);
      if (key === 'description') setDescription(value);
      // Los campos de texto se acumulan y se escriben juntos; el resto va
      // directo por `applyChange`, que además descarta los valores repetidos.
      scheduleWrite({ [key]: value, questionChoices: null, questionLogic });
      return;
    }

    const changes = { [key]: value };
    if (imageUrl !== undefined) {
      changes.imageUrl = imageUrl;
    }
    if (key === 'selectedQuestionOption') {
      changes.surveyTeleauditId = value;
    }
    applyChange(changes);
  }, [applyChange, questionLogic, scheduleWrite, setDescription, syncLocalField]);

  const handleAiAnalysisRequired = useCallback((event) => {
    const { checked } = event.target;
    setAiAnalysisRequired(checked);
    // A single-image question can only ever capture one picture.
    applyChange({ aiAnalysisRequired: checked, nMinPictures: 1 });
  }, [applyChange]);

  const handlePriceKpiChange = useCallback((event) => {
    const { checked } = event.target;
    setPriceKpiEnabled(checked);
    applyChange({ priceKpi: { teleauditType: checked ? PRICE : NONE } });
  }, [applyChange]);

  const handleGuidelineChange = useCallback((event) => {
    const { value } = event.target;
    setGuideline(value);
    applyChange({ guideline: value });
  }, [applyChange]);

  const handleKpiSave = () => {
    const shareKpi = {
      teleauditType: shareOfShelfType,
      target: Number(target),
    };
    const binaryKpis = kpiDetails.map((item) => ({
      id: item.teleauditKpiId || null,
      title: item.title,
      description: item.description,
      teleauditType: item.teleauditType,
      image: item.image || '',
    }));
    // eslint-disable-next-line no-param-reassign
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      requiresTeleaudit,
      binaryKpis,
      shareKpi,
      priceKpi: { teleauditType: priceKpiEnabled ? PRICE : NONE },
    };
  };

  const settingsModal = (currentStep) => (
    <ImageSettingsModal
      requiresTeleaudit={requiresTeleaudit}
      setRequiresTeleaudit={setRequiresTeleaudit}
      kpiDetails={kpiDetails}
      setKpiDetails={setKpiDetails}
      setOpenSettings={setOpenSettings}
      openSettings={openSettings}
      openSettingSo={openSettingSo}
      setOpenSettingSo={setOpenSettingSo}
      shareOfShelfType={shareOfShelfType}
      setShareOfShelfType={setShareOfShelfType}
      setTarget={setTarget}
      target={target}
      handleKpiSave={handleKpiSave}
      mode={mode}
      currentStep={currentStep}
    />
  );

  return (
    <QuestionShell
      {...question}
      handleOnChange={handleOnChange}
      identifier={identifier}
      info={info}
      mode={mode}
      titleKey="task.taskEdition.imageAnswerText.question"
      descriptionKey="task.taskEdition.imageAnswerText.description"
      handleDeleteQuestion={handleDeleteQuestion}
      handleDuplicateQuestion={handleDuplicateQuestion}
    >
      <div className={styles.qualityContainer}>
        <Select
          name="imageQuality"
          value={imageQuality}
          onChange={mode === EDIT_MODE ? (e) => {
            setImageQuality(e.target.value);
            handleOnChange(e);
          } : null}
          disabled={mode === VIEW_MODE}
          className={styles.imageQuality}
          disableUnderline
          displayEmpty
        >
          <MenuItem value="" disabled hidden className={styles.placeholder}>
            {t('task.taskEdition.imageAnswerText.imageQuality')}
          </MenuItem>
          <MenuItem value="low">{t('task.taskEdition.imageAnswerText.low')}</MenuItem>
          <MenuItem value="medium">{t('task.taskEdition.imageAnswerText.medium')}</MenuItem>
          <MenuItem value="high">{t('task.taskEdition.imageAnswerText.high')}</MenuItem>
          <MenuItem value="veryHigh">{t('task.taskEdition.imageAnswerText.veryHigh')}</MenuItem>
          <MenuItem value="ultra">{t('task.taskEdition.imageAnswerText.ultra')}</MenuItem>
        </Select>
      </div>
      {questionOptions && questionOptions.length > 0 && (
        <div className={styles.qualityContainer}>
          <Select
            name="questionTeleaudit"
            value={selectedQuestionOption || ''}
            onChange={(e) => {
              setSelectedQuestionOption(e.target.value);
              handleOnChange({
                target: {
                  id: 'selectedQuestionOption',
                  value: e.target.value,
                },
              });
            }}
            disabled={mode === VIEW_MODE}
            className={styles.imageQuality}
            disableUnderline
            displayEmpty
          >
            <MenuItem value="" disabled hidden className={styles.placeholder}>
              {t('task.taskEdition.questionSurveyTeleaudit')}
            </MenuItem>
            {questionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
      {surveyAiAnalysisRequired && (
        <div className={styles.aiAnalysisContainer}>
          <FormControlLabel
            className={styles.switch}
            control={
              <Switch
                checked={aiAnalysisRequired}
                onChange={mode === EDIT_MODE ? handleAiAnalysisRequired : null}
                disabled={mode === VIEW_MODE}
                color="primary"
              />
            }
            label={t('task.taskEdition.aiAnalysisQuestionSwitch')}
          />
          {aiAnalysisRequired && (
            <div className={styles.aiAnalysisFields}>
              <TextField
                id="guideline"
                label={t('task.taskEdition.aiAnalysisGuideline')}
                value={guideline}
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 255 }}
                onChange={mode === EDIT_MODE ? handleGuidelineChange : null}
                disabled={mode === VIEW_MODE}
              />
            </div>
          )}
        </div>
      )}
      <Button
        onClick={() => setOpenSettings(true)}
      >
        {t('task.taskEdition.imageAnswerText.settingOs')}
      </Button>
      {openSettings && settingsModal(1)}
      <Button
        onClick={() => setOpenSettingSo(true)}
      >
        {t('task.taskEdition.imageAnswerText.settings')}
      </Button>
      {openSettingSo && settingsModal(2)}
      <FormControlLabel
        className={styles.switch}
        control={
          <Switch
            checked={priceKpiEnabled}
            onChange={mode === EDIT_MODE ? handlePriceKpiChange : null}
            disabled={mode === VIEW_MODE}
            color="primary"
          />
        }
        label={t('task.taskForm.kpisModal.priceRequire')}
      />
    </QuestionShell>
  );
}

export default ImageAnswer;
